import { Component, EventEmitter, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DocumentService } from 'src/app/services/document/document.service';
import { UserDocumentsService, UserDocument } from 'src/app/services/user-documents/user-documents.service';
import { ConsultationService } from 'src/app/services/consultation/consultation.service';
import { DocumentMetadata } from 'src/app/utils/interfaces/new/DocumentMetadata';
import { Consultation } from 'src/app/utils/interfaces/new/consultation.interface';
import { ModalService } from 'src/app/services/modal/modal.service';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import moment from 'moment';

interface DocumentDisplay {
  id: string;
  gedId?: string;
  title: string;
  contentType: string;
  fileName: string;
  selected: boolean;
  source?: 'patient' | 'user'; // Pour différencier la source
}

interface ConsultationWithDocuments {
  consultation: Consultation;
  documents: DocumentDisplay[];
  isLoading: boolean;
  isExpanded: boolean;
}

@Component({
  selector: 'app-select-document-modal',
  templateUrl: './select-document-modal.component.html',
  styleUrls: ['./select-document-modal.component.scss']
})
export class SelectDocumentModalComponent implements OnInit {
  @Output() documentSelected = new EventEmitter<DocumentDisplay>();
  
  public patientId: string;
  public consultId: string;
  
  selectedTabIndex = 0;
  consultations: ConsultationWithDocuments[] = [];
  userDocuments: DocumentDisplay[] = [];
  allPatientDocuments: DocumentDisplay[] = [];
  
  isLoadingConsultations = false;
  isLoadingUser = false;
  isLoadingAllPatient = false;
  
  totalConsultations = 0;
  pageSize = 10;
  currentPage = 0;
  pageSizeOptions = [5, 10, 20, 50];
  
  allPatientDocsTotal = 0;
  allPatientDocsPageSize = 10;
  allPatientDocsCurrentPage = 0;
  
  userDocsTotal = 0;
  userDocsPageSize = 10;
  userDocsCurrentPage = 0;
  
  patientSearchControl = new FormControl('');
  allPatientSearchControl = new FormControl('');
  userSearchControl = new FormControl('');
  
  filteredConsultations: ConsultationWithDocuments[] = [];
  filteredAllPatientDocuments: DocumentDisplay[] = [];
  filteredUserDocuments: DocumentDisplay[] = [];
  public closeEmitter = new EventEmitter();

  constructor(
    private _documentSrv: DocumentService,
    private _userDocumentSrv: UserDocumentsService,
    private _consultationSrv: ConsultationService
  ) {}

  async ngOnInit() {
    await this.loadAllPatientDocuments();
    await this.loadConsultations();
    await this.loadUserDocuments();
    
    // abonnement aux changements de recherche
    this.patientSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.filterConsultations();
      });
    
    this.allPatientSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.allPatientDocsCurrentPage = 0;
        this.loadAllPatientDocuments(0);
      });
      
    this.userSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.userDocsCurrentPage = 0;
        this.loadUserDocuments(0);
      });
  }

  /**
   * Charger les consultations du patient
   */
  private async loadConsultations(page: number = 0) {
    this.isLoadingConsultations = true;
    try {
      const paginatedConsultations = await this._consultationSrv.find(this.patientId, {
        currentPage: page + 1, 
        perpage: this.pageSize,
        sortBy: 'date',
        sortOrder: SortEnum.DESC,
      });
      this.consultations = paginatedConsultations.objects.map(consult => ({
        consultation: consult,
        documents: [],
        isLoading: false,
        isExpanded: false
      }));
      
      this.totalConsultations = paginatedConsultations.total;
      this.filterConsultations();
    } catch (error) {
      console.error('Erreur lors du chargement des consultations:', error);
      this.consultations = [];
      this.totalConsultations = 0;
    } finally {
      this.isLoadingConsultations = false;
    }
  }
  
  /**
   * Gère le changement de page du paginator
   */
  public async onPageChange(event: any) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadConsultations(event.pageIndex);
  }

  /**
   * Charger tous les documents patient (paginé)
   */
  private async loadAllPatientDocuments(page: number = 0) {
    this.isLoadingAllPatient = true;
    try {
      const searchTerm = this.allPatientSearchControl.value?.trim();
      const documents = await this._documentSrv.searchDocuments(
        this.patientId,
        searchTerm || undefined,
        undefined,
        undefined,
        { currentPage: page + 1, perpage: this.allPatientDocsPageSize }
      );
      
      this.allPatientDocuments = documents.objects.map(doc => ({
        id: doc.id,
        title: doc.titre || doc.fileName || 'Document sans titre',
        contentType: doc.contentType,
        fileName: doc.fileName,
        selected: false,
        source: 'patient' as const,
        size: doc.weight
      }));
      
      this.allPatientDocsTotal = documents.total;
      this.filterAllPatientDocuments();
    } catch (error) {
      console.error('Erreur lors du chargement des documents patient:', error);
      this.allPatientDocuments = [];
      this.allPatientDocsTotal = 0;
    } finally {
      this.isLoadingAllPatient = false;
    }
  }

  /**
   * Gère le changement de page pour tous les documents patient
   */
  public async onAllPatientPageChange(event: any) {
    this.allPatientDocsCurrentPage = event.pageIndex;
    this.allPatientDocsPageSize = event.pageSize;
    await this.loadAllPatientDocuments(event.pageIndex);
  }

  /**
   * Filtrer tous les documents patient
   */
  private filterAllPatientDocuments() {
    this.filteredAllPatientDocuments = [...this.allPatientDocuments];
  }

  /**
   * Charger les documents d'une consultation quand on l'expand
   */
  public async onConsultationExpanded(consultWithDocs: ConsultationWithDocuments) {
    // Si déjà chargé, ne rien faire
    if (consultWithDocs.documents.length > 0) {
      return;
    }
    
    consultWithDocs.isLoading = true;
    try {
      const documents = await this._documentSrv.searchDocuments(
        this.patientId,
        undefined,
        undefined,
        undefined,
        { currentPage: 1, perpage: 100 }
      );
      
      // Filtrer les documents de cette consultation
      const consultDocuments = documents.objects.filter(
        doc => doc.consultation?.id === consultWithDocs.consultation.id
      );
      
      consultWithDocs.documents = consultDocuments.map(doc => ({
        id: doc.id,
        title: doc.titre || doc.fileName || 'Document sans titre',
        contentType: doc.contentType,
        fileName: doc.fileName,
        selected: false,
        source: 'patient' as const, // sert à déterminer plus tard si on a un doc patient ou user
        size: doc.weight
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      consultWithDocs.documents = [];
    } finally {
      consultWithDocs.isLoading = false;
    }
  }

  /**
   * Filtrer les consultations selon la recherche
   */
  private filterConsultations() {
    const searchTerm = this.patientSearchControl.value?.trim().toLowerCase();
    
    if (!searchTerm) {
      this.filteredConsultations = [...this.consultations];
    } else {
      this.filteredConsultations = this.consultations.filter(c => {
        const dateStr = moment(c.consultation.date).format('DD/MM/YYYY');
        const titleStr = c.consultation.titre?.toLowerCase() || '';
        
        return dateStr.includes(searchTerm) || titleStr.includes(searchTerm);
      });
    }
  }

  /**
   * Charger les documents utilisateur
   */
  private async loadUserDocuments(page: number = 0) {
    this.isLoadingUser = true;
    try {
      const searchTerm = this.userSearchControl.value?.trim();
      const documents = await this._userDocumentSrv.findUserDocuments({
        title: searchTerm || undefined,
        page: page + 1,
        perPage: this.userDocsPageSize
      });
      
      this.userDocuments = documents.objects.map((doc: UserDocument) => ({
        id: doc.id, 
        gedId: doc.gedId, 
        title: doc.title || doc.fileName || 'Document sans titre',
        contentType: doc.contentType,
        fileName: doc.fileName,
        selected: false,
        source: 'user' as const, // sert à déterminer plus tard si on a un document patient ou user 
        size: doc.weight
      }));
      
      this.userDocsTotal = documents.total;
      this.filterUserDocuments();
    } catch (error) {
      console.error('Erreur lors du chargement des documents utilisateur:', error);
      this.userDocuments = [];
      this.userDocsTotal = 0;
    } finally {
      this.isLoadingUser = false;
    }
  }

  /**
   * Gère le changement de page pour les documents utilisateur
   */
  public async onUserPageChange(event: any) {
    this.userDocsCurrentPage = event.pageIndex;
    this.userDocsPageSize = event.pageSize;
    await this.loadUserDocuments(event.pageIndex);
  }

  /**
   * Filtrer les documents utilisateur
   */
  private filterUserDocuments() {
    const searchTerm = this.userSearchControl.value?.trim().toLowerCase();
    
    if (!searchTerm) {
      this.filteredUserDocuments = [...this.userDocuments];
    } else {
      this.filteredUserDocuments = this.userDocuments.filter(doc =>
        doc.title.toLowerCase().includes(searchTerm) ||
        doc.fileName.toLowerCase().includes(searchTerm)
      );
    }
  }

  /**
   * Toggle sélection d'un document
   */
  public toggleDocumentSelection(doc: DocumentDisplay) {
    doc.selected = !doc.selected;
  }

  /**
   * Compter les documents sélectionnés
   */
  public get selectedDocumentsCount(): number {
    let count = 0;
    
    // Compter dans tous les documents patient
    count += this.allPatientDocuments.filter(d => d.selected).length;
    
    // Compter dans consultations
    this.consultations.forEach(c => {
      count += c.documents.filter(d => d.selected).length;
    });
    
    // Compter dans documents utilisateur
    count += this.userDocuments.filter(d => d.selected).length;
    
    return count;
  }

  /**
   * Obtenir tous les documents sélectionnés
   */
  public getSelectedDocuments(): DocumentDisplay[] {
    const selected: DocumentDisplay[] = [];
    
    // Documents patient (tous)
    selected.push(...this.allPatientDocuments.filter(d => d.selected));
    
    // Documents patient (par consultation)
    this.consultations.forEach(c => {
      selected.push(...c.documents.filter(d => d.selected));
    });
    
    // Documents utilisateur
    selected.push(...this.userDocuments.filter(d => d.selected));
    
    return selected;
 }

  /**
   * Ajouter tous les documents sélectionnés
   */
  public addSelectedDocuments() {
    const selectedDocs = this.getSelectedDocuments();
    this.documentSelected.emit(selectedDocs as any);
  }

  /**
   * Formatter la date de consultation
   */
  public formatConsultationDate(date: Date): string {
    return moment(date).format('DD/MM/YYYY');
  }

  public closeModal() {
    this.closeEmitter.emit();
  }
}
